import React, { useState, useRef, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

const CropContainer = styled('div')({
	position: 'relative',
	width: '100%',
	maxWidth: '500px',
	margin: '0 auto',
	backgroundColor: '#f5f5f5',
	borderRadius: '8px',
	overflow: 'hidden',
	cursor: 'move',
	userSelect: 'none'
});

const ImageCanvas = styled('canvas')({
	display: 'block',
	width: '100%',
	height: 'auto'
});

const CropCircle = styled('div')({
	position: 'absolute',
	border: '3px solid #1976d2',
	borderRadius: '50%',
	boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
	cursor: 'move',
	pointerEvents: 'auto',
	'&::before': {
		content: '""',
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		width: '4px',
		height: '4px',
		backgroundColor: '#1976d2',
		borderRadius: '50%'
	}
});

const Instructions = styled(Typography)({
	marginBottom: '16px',
	color: '#666',
	fontSize: '14px',
	textAlign: 'center'
});

const AvatarCropDialog = ({ open, imageSrc, onClose, onCrop }) => {
	const [cropSize, setCropSize] = useState(200); // Initial crop circle size
	const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
	const [, setScale] = useState(1);
	
	const containerRef = useRef(null);
	// const imageRef = useRef(null);
	const canvasRef = useRef(null);

    const getCanvasRect = () => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        return canvas.getBoundingClientRect(); // CSS px
      };
      
	// Load and display image
	useEffect(() => {
		if (!imageSrc || !open) return;

		const img = new Image();
		img.onload = () => {
			// Calculate display size (max 500px width)
			const maxWidth = 500;
			const displayWidth = Math.min(img.width, maxWidth);
			const displayHeight = (img.height / img.width) * displayWidth;
			
			setImageSize({ width: img.width, height: img.height });
			setScale(img.width / displayWidth);

			// Set canvas size
			if (canvasRef.current) {
				canvasRef.current.width = displayWidth;
				canvasRef.current.height = displayHeight;
				const ctx = canvasRef.current.getContext('2d');
				ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
			}

			// Center the crop circle
			const initialX = displayWidth / 2;
			const initialY = displayHeight / 2;
			setCropPosition({ x: initialX, y: initialY });

			// Set initial crop size (60% of smaller dimension)
			const minDimension = Math.min(displayWidth, displayHeight);
			setCropSize((minDimension * 0.7));
		};
		img.src = imageSrc;
	}, [imageSrc, open]);

	// Handle mouse/touch events for dragging
	const handleMouseDown = (e) => {
        const rect = getCanvasRect();
        if (!rect) return;

        const clientX = e.clientX ?? e.touches?.[0]?.clientX;
        const clientY = e.clientY ?? e.touches?.[0]?.clientY;
        if (clientX == null || clientY == null) return;

        const x = clientX - rect.left;
        const y = clientY - rect.top;

        const dx = x - cropPosition.x;
        const dy = y - cropPosition.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance <= cropSize / 2) {
        setIsDragging(true);
        setDragStart({ x: x - cropPosition.x, y: y - cropPosition.y });
        }
    };

	// Add event listeners for dragging
	useEffect(() => {
		if (!isDragging) return;

		const handleMouseMove = (e) => {
            const rect = getCanvasRect();
            if (!rect) return;
          
            const clientX = e.clientX ?? e.touches?.[0]?.clientX;
            const clientY = e.clientY ?? e.touches?.[0]?.clientY;
            if (clientX == null || clientY == null) return;
          
            // Prevent page scroll on touch drag
            if (e.cancelable) e.preventDefault();
          
            let x = clientX - rect.left - dragStart.x;
            let y = clientY - rect.top - dragStart.y;
          
            // Constrain inside rendered canvas bounds (CSS px)
            const maxX = rect.width;
            const maxY = rect.height;
          
            x = Math.max(cropSize / 2, Math.min(x, maxX - cropSize / 2));
            y = Math.max(cropSize / 2, Math.min(y, maxY - cropSize / 2));
          
            setCropPosition({ x, y });
          };
          
		const handleMouseUp = () => {
			setIsDragging(false);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
		document.addEventListener('touchmove', handleMouseMove, { passive: false });
		document.addEventListener('touchend', handleMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			document.removeEventListener('touchmove', handleMouseMove);
			document.removeEventListener('touchend', handleMouseUp);
		};
	}, [isDragging, dragStart, cropSize]);

	// Handle crop size change with mouse wheel
	const handleWheel = (e) => {
        e.preventDefault();
      
        const rect = getCanvasRect();
        if (!rect) return;
      
        const delta = e.deltaY > 0 ? -10 : 10;
        const maxCrop = Math.min(rect.width, rect.height) * 0.95;
        const minCrop = 120;
      
        const newSize = Math.max(minCrop, Math.min(maxCrop, cropSize + delta));
      
        let x = cropPosition.x;
        let y = cropPosition.y;
      
        x = Math.max(newSize / 2, Math.min(x, rect.width - newSize / 2));
        y = Math.max(newSize / 2, Math.min(y, rect.height - newSize / 2));
      
        setCropSize(newSize);
        setCropPosition({ x, y });
      };

	// Crop and process image
	const handleCrop = () => {
        if (!canvasRef.current || !imageSize.width) return;
      
        const rect = getCanvasRect();
        if (!rect) return;
      
        // Scale from rendered canvas (CSS px) to original image px
        const scaleX = imageSize.width / rect.width;
        const scaleY = imageSize.height / rect.height;
      
        // Output canvas (300x300)
        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = 300;
        outputCanvas.height = 300;
        const outputCtx = outputCanvas.getContext('2d');
      
        // Convert crop circle from CSS px → original image px
        const sourceX = (cropPosition.x - cropSize / 2) * scaleX;
        const sourceY = (cropPosition.y - cropSize / 2) * scaleY;
        const sourceSize = cropSize * scaleX; // (scaleX == scaleY usually)
      
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = sourceSize;
        tempCanvas.height = sourceSize;
        const tempCtx = tempCanvas.getContext('2d');
      
        tempCtx.beginPath();
        tempCtx.arc(sourceSize / 2, sourceSize / 2, sourceSize / 2, 0, 2 * Math.PI);
        tempCtx.clip();
      
        const img = new Image();
        img.onload = () => {
          tempCtx.drawImage(
            img,
            sourceX, sourceY, sourceSize, sourceSize,
            0, 0, sourceSize, sourceSize
          );
      
          outputCtx.drawImage(tempCanvas, 0, 0, 300, 300);
          const dataUrl = outputCanvas.toDataURL('image/png');
          onCrop(dataUrl);
          onClose();
        };
        img.src = imageSrc;
      };

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					minWidth: '400px'
				}
			}}
		>
			<DialogTitle>
				Crop Profile Picture
			</DialogTitle>
			<DialogContent>
				<Instructions>
					Drag the circle to select the area. Use mouse wheel to resize. Recommended: 300x300 pixels
				</Instructions>
				<CropContainer
					ref={containerRef}
					onMouseDown={handleMouseDown}
					onTouchStart={handleMouseDown}
					onWheel={handleWheel}
				>
					<ImageCanvas ref={canvasRef} />
					<CropCircle
						style={{
							left: `${cropPosition.x - cropSize / 2}px`,
							top: `${cropPosition.y - cropSize / 2}px`,
							width: `${cropSize}px`,
							height: `${cropSize}px`
						}}
					/>
				</CropContainer>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button onClick={handleCrop} variant="contained" color="primary">
					Crop & Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AvatarCropDialog;

